import {
  esrsSustainabilityMatters,
  isEsrsMatter,
} from '@upright/common/model/input/materiality-assessment/matters/esrs-matters';
import {
  isRelevantUprightMatter,
  isUprightMatter,
  relevantUprightMatterIds,
  uprightSustainabilityMatters,
} from '@upright/common/model/input/materiality-assessment/matters/upright-matters';
import {
  allSustainabilityMatterIdsHash,
  CsrdMatterHierarchy,
  legacySustainabilitMatterIds,
  SustainabilityMatter,
  SustainabilityMatterId,
} from '@upright/common/model/input/materiality-assessment/types-matters';
import {
  csrdTopCategoryIds,
  CsrdTopCategoryIdWithEsId,
  CsrdTopicIdWithEsId,
  EsTopicsId,
  esTopicsId,
  isCsrdEsrsTopicId,
  isCsrdTopCategoryId,
} from '@upright/common/model/output/materiality/types';
export function isLegacyMatterId(id: SustainabilityMatterId): boolean {
  return legacySustainabilitMatterIds.has(id);
}

/**
 * The matter tree, indexed once (PLT-6841).
 *
 * Every helper in this file used to rebuild what it needed from the whole list on each call — a
 * `groupBy` in `getSustainabilityMatterChildren`, a `keyBy` in `getMatterById` — and the recursive
 * ones call each other, so `getTopCategoryForLeafMatterId` walked the tree once per category for every
 * matter it was asked about, and `isSustainabilityMatterLeaf` regrouped the whole list for every result
 * part a read filtered. On one supplier risk read of a large company that was over half the read's CPU
 * (a V8 profile of Tesla on a dev env), all of it re-deriving a structure that never changes: the
 * matters are module constants.
 *
 * Built on first use rather than at import, because `upright-matters.ts` and `esrs-matters.ts` import
 * this module back. The indexes are private and every array handed out is a copy, so a caller that
 * sorts or pushes onto one cannot change the next caller's answer — each call used to return a fresh
 * array, and callers may rely on owning it.
 */
interface MatterIndex {
  /** In the order `getSustainabilityMatters` has always returned: ESRS, then Upright. */
  matters: readonly SustainabilityMatter[];
  byId: ReadonlyMap<SustainabilityMatterId, SustainabilityMatter>;
  childrenByParent: ReadonlyMap<SustainabilityMatterId, readonly SustainabilityMatter[]>;
  /** Filled as ids are asked about. */
  leavesById: Map<SustainabilityMatterId | EsTopicsId, readonly SustainabilityMatterId[]>;
  topCategoryByLeaf: Map<SustainabilityMatterId, CsrdTopCategoryIdWithEsId>;
}

let matterIndex: MatterIndex | undefined;

function getMatterIndex(): MatterIndex {
  if (matterIndex) return matterIndex;

  const matters = [esrsSustainabilityMatters, uprightSustainabilityMatters]
    .flat()
    .filter(matter => !isLegacyMatterId(matter.id));
  const childrenByParent = new Map<SustainabilityMatterId, SustainabilityMatter[]>();
  for (const matter of matters) {
    if (matter.parent === undefined) continue;
    const siblings = childrenByParent.get(matter.parent);
    if (siblings) siblings.push(matter);
    else childrenByParent.set(matter.parent, [matter]);
  }

  matterIndex = {
    matters,
    // Last one wins on a repeated id, as `keyBy` did.
    byId: new Map(matters.map(matter => [matter.id, matter])),
    childrenByParent,
    leavesById: new Map(),
    topCategoryByLeaf: new Map(),
  };
  return matterIndex;
}

export function getSustainabilityMatters() {
  // Note the order is expected to be [ESRS, Upright]
  return [...getMatterIndex().matters];
}

export function getMatterById(id: SustainabilityMatterId): SustainabilityMatter {
  const matter = getMatterIndex().byId.get(id);
  if (!matter) {
    throw Error(`Unknown sustainability matter ID: ${id}`);
  }
  return matter;
}

export function friendlySustainabilityMatterTitle(id: SustainabilityMatterId | EsTopicsId, withQualifier?: boolean) {
  if (id === esTopicsId) return 'Entity-specific topics';
  const matter = getMatterById(id);
  if (!withQualifier || !matter.qualifier) return matter.title;
  return `${matter.title} (${matter.qualifier.toLowerCase()})`;
}

export function getTopLevelSustainabilityMatters(hierarchy: CsrdMatterHierarchy) {
  const topLevelMatters = getSustainabilityMatters().filter(d => d.parent === undefined);
  return topLevelMatters.filter(d => d.hierarchy === hierarchy);
}

export function getSustainabilityMatterChildren(id: SustainabilityMatterId | EsTopicsId): SustainabilityMatter[] {
  if (id === esTopicsId) return getSustainabilityMatterLeafs(esTopicsId).map(getMatterById);

  return [...(getMatterIndex().childrenByParent.get(id) ?? [])];
}

export function getRelevantSustainabilityMatterLeaves(matterId: SustainabilityMatterId): SustainabilityMatterId[] {
  return getSustainabilityMatterLeafs(matterId).filter(isRelevantMatter);
}

export function getAllRelevantLeafMatterIds(): SustainabilityMatterId[] {
  return getSustainabilityMatters()
    .map(d => d.id)
    .filter(d => getSustainabilityMatterChildren(d).length === 0)
    .filter(isRelevantMatter);
}

export function getSustainabilityMatterLeafs(id: SustainabilityMatterId | EsTopicsId): SustainabilityMatterId[] {
  return [...leavesOf(id)];
}

/** {@link getSustainabilityMatterLeafs} without the copy, for this file's own callers. */
function leavesOf(id: SustainabilityMatterId | EsTopicsId): readonly SustainabilityMatterId[] {
  const { byId, leavesById } = getMatterIndex();
  const known = leavesById.get(id);
  if (known) return known;

  const leaves = computeLeaves(id);
  // Only ids in the tree are remembered, which bounds the memo at the tree's size. An unknown id is
  // its own leaf, and ids reach here from request input — `?matterId=` on the aggregated-evidence
  // route is validated only as a string — so remembering those would grow without end (PR #8783 review).
  if (id === esTopicsId || byId.has(id)) leavesById.set(id, leaves);
  return leaves;
}

function computeLeaves(id: SustainabilityMatterId | EsTopicsId): SustainabilityMatterId[] {
  if (id === esTopicsId) {
    // OA: A bit awkward, but adding a new parent matterId for upright matters would cascade to fixes elsewhere, while this
    // solution doesn't.
    return relevantUprightMatterIds.filter(isSustainabilityMatterLeaf);
  }

  const children = getMatterIndex().childrenByParent.get(id) ?? [];
  if (children.length === 0) {
    return [id];
  }
  return children.flatMap(d => leavesOf(d.id));
}

export function isSustainabilityMatterLeaf(id: SustainabilityMatterId | EsTopicsId): boolean {
  // The entity-specific topics have children without being a parent in the tree: they are every
  // Upright leaf, as `getSustainabilityMatterChildren` answers. So they are not a leaf, which is what
  // the per-call walk this replaced said too (PR #8783 review).
  if (id === esTopicsId) return false;
  return !getMatterIndex().childrenByParent.has(id);
}

export function getSustainabilityMatterParents(id: SustainabilityMatterId): SustainabilityMatterId[] {
  const parents: SustainabilityMatterId[] = [];
  let current = id;
  let count = 0;
  while (count < 10) {
    count++;
    const parent = getMatterById(current).parent;
    if (parent === undefined) {
      return parents;
    } else {
      parents.push(parent);
    }
    current = parent;
  }
  return parents;
}

export function getParentTopicId(leafMatterId: SustainabilityMatterId): CsrdTopicIdWithEsId {
  const parentIds = getSustainabilityMatterParents(leafMatterId);
  if (parentIds.length === 0) throw Error(`No parents found for leaf matter ${leafMatterId}`);

  if (isUprightMatter(leafMatterId)) return esTopicsId;

  const topicLevelParentId = parentIds.find(parent => isCsrdEsrsTopicId(parent));
  if (!topicLevelParentId) throw Error(`No parent topic found for matter ${leafMatterId}`);

  return topicLevelParentId;
}

export function* getFlattenedSustainabilityMatterIds() {
  const topLevelMatters = getSustainabilityMatters().filter(d => d.parent === undefined);
  for (const topLevelMatter of topLevelMatters) {
    for (const leafMatter of getSustainabilityMatterLeafs(topLevelMatter.id)) {
      yield leafMatter;
    }
  }
}

export function isSustainabilityMatterId(value: string): value is SustainabilityMatterId {
  return Boolean(allSustainabilityMatterIdsHash[value]);
}

export function assertSustainabilityMatterId(value: string): asserts value is SustainabilityMatterId {
  if (!isSustainabilityMatterId(value)) {
    throw Error(`Unknown matterId: ${value}`);
  }
}

export function convertLegacyMatterId(id: SustainabilityMatterId): SustainabilityMatterId {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (id) {
    // OA: These two are flipped on the sheet. Delete these lines once sheet is fixed.
    case SustainabilityMatterId.E1_CLIMATE_CHANGE_ADAPTATION:
      return SustainabilityMatterId.E1_CLIMATE_CHANGE_MITIGATION;
    case SustainabilityMatterId.E1_CLIMATE_CHANGE_MITIGATION:
      return SustainabilityMatterId.E1_CLIMATE_CHANGE_ADAPTATION;

    case SustainabilityMatterId.LEGACY_E5_RESOURCES_OUTFLOWS:
    case SustainabilityMatterId.LEGACY_E5_WASTE:
      return SustainabilityMatterId.E5_RESOURCE_OUTFLOWS_AND_WASTE;
    case SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_SOIL:
    case SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_WATER:
    case SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_LIVING_ORGANISMS_AND_FOOD_RESOURCES:
      return SustainabilityMatterId.E2_POLLUTION_OF_WATER_AND_SOIL;
    case SustainabilityMatterId.LEGACY_E3_WATER_CONSUMPTION:
    case SustainabilityMatterId.LEGACY_E3_WATER_WITHDRAWALS:
      return SustainabilityMatterId.E3_WATER_WITHDRAWALS_AND_CONSUMPTION;
    case SustainabilityMatterId.LEGACY_E3_WATER_DISCHARGES_IN_THE_OCEANS:
      return SustainabilityMatterId.E3_WATER_DISCHARGES;
    case SustainabilityMatterId.LEGACY_E4_IMPACTS_ON_THE_EXTENT_AND_CONDITION_OF_ECOSYSTEMS:
    case SustainabilityMatterId.LEGACY_E4_IMPACTS_ON_THE_STATE_OF_SPECIES:
      return SustainabilityMatterId.E4_ECOSYSTEMS_AND_SPECIES;
    default:
      return id;
  }
}

export function isRelevantMatter(matterId: SustainabilityMatterId) {
  return isEsrsMatter(matterId) || isRelevantUprightMatter(matterId);
}

export function isRelevantLeafMatter(matterId: SustainabilityMatterId) {
  return isRelevantMatter(matterId) && isSustainabilityMatterLeaf(matterId);
}

/**
 * Whether an assessment can hold a result for this matter — i.e. whether any DMA surface would ever
 * render a row for it. `SustainabilityMatterId` is much wider than that: it also holds the topic
 * headings above the assessable matters, the Upright matters that duplicate an ESRS leaf, and
 * retired legacy ids. Use this whenever an id arrives from outside (API payload, agent tool call)
 * and something is about to be stored against it — see PLT-6774.
 *
 * Total where `isRelevantLeafMatter` is not: that one reaches `getMatterById`, which throws for a
 * legacy id, and a throw inside a zod refinement escapes `safeParse` and turns a 400 into a 500.
 */
export function isAssessableLeafMatter(matterId: SustainabilityMatterId): boolean {
  try {
    return isRelevantLeafMatter(matterId);
  } catch {
    return false;
  }
}

/**
 * The assessable leaf matters below `matterId`, for telling a caller which matters it could have
 * meant. Empty when the id is a leaf already, is legacy, or holds nothing assessable.
 */
export function getAssessableLeafMattersUnder(matterId: SustainabilityMatterId): SustainabilityMatterId[] {
  try {
    return getSustainabilityMatterLeafs(matterId)
      .filter(id => id !== matterId)
      .filter(isAssessableLeafMatter);
  } catch {
    return [];
  }
}

export function getTopCategoryForLeafMatterId(leafMatterId: SustainabilityMatterId): CsrdTopCategoryIdWithEsId {
  if (isUprightMatter(leafMatterId)) return esTopicsId;

  if (isCsrdTopCategoryId(leafMatterId)) {
    return leafMatterId;
  }

  const { topCategoryByLeaf } = getMatterIndex();
  const known = topCategoryByLeaf.get(leafMatterId);
  if (known) return known;

  for (const topCategoryId of csrdTopCategoryIds) {
    if (leavesOf(topCategoryId).includes(leafMatterId)) {
      topCategoryByLeaf.set(leafMatterId, topCategoryId);
      return topCategoryId;
    }
  }

  throw new Error(`No top category found for ${leafMatterId}`);
}

export function friendlyTopCategory(topic: SustainabilityMatterId | EsTopicsId) {
  if (topic === esTopicsId) return 'Entity-Specific';

  return friendlySustainabilityMatterTitle(topic);
}
